// AddCheckNoteModal.tsx - Çek/Senet Ekleme/Düzenleme Modalı
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Divider,
  InputAdornment,
  CircularProgress,
  Autocomplete,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionIcon from '@mui/icons-material/Description';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  CheckNoteDto,
  CreateCheckNoteDto,
  UpdateCheckNoteDto,
  CheckNoteType,
  CheckNoteDirection,
  CheckNoteTypeValue,
  CheckNoteDirectionValue,
  createCheckNote,
  updateCheckNote,
  checkDuplicateCheckNumber
} from '../../../services/checkNoteService';
import { checkBookService, CheckBook } from '../../../services/checkBookService';
import CurrencyService from '../../../services/CurrencyService';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import ProjectSelector from '../shared/ProjectSelector';
import { AppDatePicker } from '../../common/AppDatePicker';
import GuideButton from '../../common/guide/GuideButton';

interface AddCheckNoteModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  type: CheckNoteTypeValue;
  companyId: string;
  editData?: CheckNoteDto | null;
}

interface Account {
  id: number;
  name: string;
  code: string;
}

// Çek hesabı interface (banka hesapları + alt hesaplar)
interface CheckAccountOption {
  id: number;
  name: string;
  bankName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  isSubAccount: boolean;
  parentBankAccountId?: number;
  subAccountId?: number;
}

const AddCheckNoteModal: React.FC<AddCheckNoteModalProps> = ({
  open,
  onClose,
  onSave,
  type,
  companyId,
  editData
}) => {
  const { t } = useTranslation('common2');
  const isEdit = !!editData;
  
  // Form state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  
  // Verilen çek için ek state'ler
  const [checkAccounts, setCheckAccounts] = useState<CheckAccountOption[]>([]);
  const [loadingCheckAccounts, setLoadingCheckAccounts] = useState(false);
  const [selectedCheckAccount, setSelectedCheckAccount] = useState<CheckAccountOption | null>(null);
  const [checkBooks, setCheckBooks] = useState<CheckBook[]>([]);
  const [loadingCheckBooks, setLoadingCheckBooks] = useState(false);
  const [selectedCheckBook, setSelectedCheckBook] = useState<CheckBook | null>(null);
  const [nextCheckNumber, setNextCheckNumber] = useState<number | null>(null);
  
  // TCMB kur çekme için state'ler
  const [loadingExchangeRate, setLoadingExchangeRate] = useState(false);
  
  // Form alanları
  const [formData, setFormData] = useState({
    documentNumber: '',
    direction: CheckNoteDirection.RECEIVED as CheckNoteDirectionValue,
    accountId: 0,
    bankName: '',
    bankBranchName: '',
    bankBranchCode: '',
    checkNumber: '',
    checkAccountNumber: '',
    drawerName: '',
    guarantorName: '',
    guarantorIdentityNo: '',
    guarantorAddress: '',
    debtorIdentityNo: '',
    debtorAddress: '',
    jurisdictionPlace: '',
    issueDate: new Date().toISOString().split('T')[0],
    issuePlace: '',
    dueDate: new Date().toISOString().split('T')[0],
    amount: 0,
    currency: 'TRY',
    exchangeRate: 1,
    notes: '',
    // PYS: bağlı olduğu proje (opsiyonel)
    projectId: null as number | null,
    // Verilen çek için
    bankAccountId: undefined as number | undefined,
    subAccountId: undefined as number | undefined,
    checkBookId: undefined as number | undefined
  });
  
  // Tutar alanı için ayrı string state (0 takılma sorununu önler)
  const [amountDisplay, setAmountDisplay] = useState<string>('');
  
  // Çek numarası mükerrer kontrolü
  const [checkNumberDuplicate, setCheckNumberDuplicate] = useState(false);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [firstAvailableCheckNumber, setFirstAvailableCheckNumber] = useState<string | null>(null);
  const [checkNumberOutOfRange, setCheckNumberOutOfRange] = useState(false);

  // Form değişiklik takibi ve kapatma onayı
  const [formDirty, setFormDirty] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  
  // Çek hesaplarını yükle (Direction = GIVEN için)
  useEffect(() => {
    const loadCheckAccounts = async () => {
      if (!companyId || type !== CheckNoteType.CHECK) return;
      
      setLoadingCheckAccounts(true);
      try {
        const response = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY);
        // API yanıtı { success, data } veya direkt array olabilir
        const bankAccounts = response.data?.data || response.data;
        if (bankAccounts && Array.isArray(bankAccounts)) {
          const options: CheckAccountOption[] = [];
          
          bankAccounts.forEach((bank: any) => {
            // Ana hesap çek hesabı mı?
            if (bank.isCheckAccount) {
              options.push({
                id: bank.id,
                name: bank.bankName,
                bankName: bank.bankName,
                accountNumber: bank.accountNumber,
                iban: bank.iban,
                currency: bank.currency,
                isSubAccount: false
              });
            }
            
            // Alt hesaplar
            if (bank.subAccounts) {
              Object.values(bank.subAccounts).forEach((sub: any) => {
                if (sub.isCheckAccount) {
                  options.push({
                    id: sub.id,
                    name: `${bank.bankName} - ${sub.accountName}`,
                    bankName: bank.bankName,
                    accountNumber: sub.accountNumber,
                    iban: sub.iban || bank.iban,
                    currency: sub.currency || bank.currency,
                    isSubAccount: true,
                    parentBankAccountId: bank.id,
                    subAccountId: sub.id
                  });
                }
              });
            }
          });
          
          setCheckAccounts(options);
        }
      } catch (err) {
        console.error('Çek hesapları yüklenemedi:', err);
      } finally {
        setLoadingCheckAccounts(false);
      }
    };
    
    if (open) {
      loadCheckAccounts();
    }
  }, [open, companyId, type]);
  
  // Seçilen çek hesabının çek defterlerini yükle
  useEffect(() => {
    const loadCheckBooks = async () => {
      if (!selectedCheckAccount || !companyId) {
        setCheckBooks([]);
        setSelectedCheckBook(null);
        return;
      }
      
      setLoadingCheckBooks(true);
      try {
        const allCheckBooks = await checkBookService.getByCompany(companyId);
        
        // Seçilen hesaba ait çek defterlerini filtrele
        const filtered = allCheckBooks.filter(cb => {
          if (selectedCheckAccount.isSubAccount) {
            return cb.subAccountId === selectedCheckAccount.subAccountId;
          } else {
            return cb.bankAccountId === selectedCheckAccount.id && !cb.subAccountId;
          }
        }).filter(cb => cb.isActive && (cb.totalLeaves - cb.usedLeaves) > 0); // Aktif ve boş yaprağı olan defterler
        
        setCheckBooks(filtered);
        
        // Tek defter varsa otomatik seç
        if (filtered.length === 1) {
          handleCheckBookSelect(filtered[0]);
        }
      } catch (err) {
        console.error('Çek defterleri yüklenemedi:', err);
      } finally {
        setLoadingCheckBooks(false);
      }
    };
    
    loadCheckBooks();
  }, [selectedCheckAccount, companyId]);
  
  // Çek hesabı seçildiğinde
  const handleCheckAccountSelect = (account: CheckAccountOption | null) => {
    setSelectedCheckAccount(account);
    setSelectedCheckBook(null);
    setNextCheckNumber(null);
    
    if (account) {
      setFormData(prev => ({
        ...prev,
        bankAccountId: account.isSubAccount ? account.parentBankAccountId : account.id,
        subAccountId: account.isSubAccount ? account.subAccountId : undefined,
        bankName: account.bankName,
        checkAccountNumber: account.accountNumber,
        currency: account.currency
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        bankAccountId: undefined,
        subAccountId: undefined,
        bankName: '',
        checkAccountNumber: '',
        checkBookId: undefined
      }));
    }
  };
  
  // Çek defteri seçildiğinde
  const handleCheckBookSelect = (checkBook: CheckBook | null) => {
    setSelectedCheckBook(checkBook);
    
    if (checkBook) {
      const nextNumber = checkBook.startNumber + checkBook.usedLeaves;
      setNextCheckNumber(nextNumber);
      
      setFormData(prev => ({
        ...prev,
        checkBookId: checkBook.id,
        checkNumber: nextNumber.toString()
      }));
    } else {
      setNextCheckNumber(null);
      setFormData(prev => ({
        ...prev,
        checkBookId: undefined,
        checkNumber: ''
      }));
    }
  };
  
  // Cari hesapları yükle
  useEffect(() => {
    const loadAccounts = async () => {
      if (!companyId) return;
      
      setLoadingAccounts(true);
      try {
        const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
        // API yanıtı { success, data } veya direkt array olabilir 
        const accountsData = response.data?.data || response.data?.Data || response.data;
        if (accountsData && Array.isArray(accountsData)) {
          setAccounts(accountsData.map((acc: any) => ({
            id: acc.Id || acc.id,
            name: acc.CompanyName || acc.companyName || acc.Name || acc.name,
            code: acc.AccountCode || acc.accountCode || acc.Code || acc.code || ''
          })));
        }
      } catch (err) {
        console.error('Cari hesaplar yüklenemedi:', err);
      } finally {
        setLoadingAccounts(false);
      }
    };
    
    if (open) {
      loadAccounts();
    }
  }, [open, companyId]);
  
  // Düzenleme modunda formu doldur
  useEffect(() => {
    if (editData && open) {
      setFormData({
        documentNumber: editData.documentNumber || '',
        direction: editData.direction,
        accountId: editData.accountId,
        bankName: editData.bankName || '',
        bankBranchName: editData.bankBranchName || '',
        bankBranchCode: editData.bankBranchCode || '',
        checkNumber: editData.checkNumber || '',
        checkAccountNumber: editData.checkAccountNumber || '',
        drawerName: editData.drawerName || '',
        guarantorName: editData.guarantorName || '',
        guarantorIdentityNo: editData.guarantorIdentityNo || '',
        guarantorAddress: editData.guarantorAddress || '',
        debtorIdentityNo: editData.debtorIdentityNo || '',
        debtorAddress: editData.debtorAddress || '',
        jurisdictionPlace: editData.jurisdictionPlace || '',
        issueDate: editData.issueDate ? editData.issueDate.split('T')[0] : new Date().toISOString().split('T')[0],
        issuePlace: editData.issuePlace || '',
        dueDate: editData.dueDate ? editData.dueDate.split('T')[0] : new Date().toISOString().split('T')[0],
        amount: editData.amount,
        currency: editData.currency || 'TRY',
        exchangeRate: editData.exchangeRate || 1,
        notes: editData.notes || '',
        projectId: editData.projectId ?? null,
        bankAccountId: undefined,
        subAccountId: undefined,
        checkBookId: undefined
      });
      setAmountDisplay(editData.amount ? String(editData.amount) : '');
      setFormDirty(false);
      
      // Seçili cari hesabı ayarla
      if (editData.accountId) {
        const account = accounts.find(a => a.id === editData.accountId);
        if (account) {
          setSelectedAccount(account);
        }
      }
    } else if (!editData && open) {
      // Yeni kayıt için formu sıfırla
      setFormData({
        documentNumber: '',
        direction: CheckNoteDirection.RECEIVED,
        accountId: 0,
        bankName: '',
        bankBranchName: '',
        bankBranchCode: '',
        checkNumber: '',
        checkAccountNumber: '',
        drawerName: '',
        guarantorName: '',
        guarantorIdentityNo: '',
        guarantorAddress: '',
        debtorIdentityNo: '',
        debtorAddress: '',
        jurisdictionPlace: '',
        issueDate: new Date().toISOString().split('T')[0],
        issuePlace: '',
        dueDate: new Date().toISOString().split('T')[0],
        amount: 0,
        currency: 'TRY',
        exchangeRate: 1,
        notes: '',
        projectId: null,
        bankAccountId: undefined,
        subAccountId: undefined,
        checkBookId: undefined
      });
      setAmountDisplay('');
      setSelectedAccount(null);
      setSelectedCheckAccount(null);
      setSelectedCheckBook(null);
      setNextCheckNumber(null);
      setFormDirty(false);
    }
  }, [editData, open, accounts]);
  
  // Form değişikliği
  const handleChange = async (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setFormDirty(true);
    
    // Para birimi değiştiğinde TCMB'den kuru çek
    if (field === 'currency') {
      if (value === 'TRY') {
        setFormData(prev => ({ ...prev, exchangeRate: 1 }));
      } else {
        setLoadingExchangeRate(true);
        try {
          const currencyRate = await CurrencyService.getCurrencyRate(value);
          const avgRate = currencyRate ? (currencyRate.buying + currencyRate.selling) / 2 : 1;
          setFormData(prev => ({ ...prev, exchangeRate: avgRate }));
        } catch (err) {
          console.error('TCMB kur çekilemedi:', err);
        } finally {
          setLoadingExchangeRate(false);
        }
      }
    }
    
    // Yön değiştiğinde verilen çek state'lerini sıfırla
    if (field === 'direction') {
      if (value === CheckNoteDirection.RECEIVED) {
        setSelectedCheckAccount(null);
        setSelectedCheckBook(null);
        setNextCheckNumber(null);
        setFormData(prev => ({
          ...prev,
          [field]: value,
          bankAccountId: undefined,
          subAccountId: undefined,
          checkBookId: undefined,
          bankName: '',
          checkAccountNumber: '',
          checkNumber: ''
        }));
      }
    }
  };
  
  // TCMB'den güncel kuru çek (manuel tetikleme için)
  const handleRefreshExchangeRate = async () => {
    if (formData.currency === 'TRY') return;
    
    setLoadingExchangeRate(true);
    try {
      const currencyRate = await CurrencyService.getCurrencyRate(formData.currency);
      const avgRate = currencyRate ? (currencyRate.buying + currencyRate.selling) / 2 : 1;
      setFormData(prev => ({ ...prev, exchangeRate: avgRate }));
    } catch (err) {
      console.error('TCMB kur çekilemedi:', err);
      setError(t('checksNotes.errors.exchangeRateFetchFailed'));
    } finally {
      setLoadingExchangeRate(false);
    }
  };
  
  // Cari hesap seçimi
  const handleAccountChange = (account: Account | null) => {
    setSelectedAccount(account);
    setFormData(prev => ({
      ...prev,
      accountId: account?.id || 0
    }));
  };
  
  // Kapatma işlemi (değişiklik kontrolü ile)
  const handleClose = () => {
    if (formDirty) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    setFormDirty(false);
    onClose();
  };

  // Çek numarası mükerrer kontrolü (onBlur)
  const handleCheckNumberBlur = async () => {
    const checkNumber = formData.checkNumber?.trim();
    if (!checkNumber || !companyId) {
      setCheckNumberDuplicate(false);
      return;
    }
    
    setCheckingDuplicate(true);
    try {
      const result = await checkDuplicateCheckNumber(
        checkNumber,
        type,
        isEdit && editData ? editData.id : undefined
      );
      setCheckNumberDuplicate(result.isDuplicate);
      setFirstAvailableCheckNumber(result.firstAvailableNumber);
    } catch (err) {
      console.error('Mükerrer kontrol hatası:', err);
    } finally {
      setCheckingDuplicate(false);
    }
  };
  
  // Kaydet
  const handleSave = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Verilen çek için validasyonlar
      const isGivenCheck = formData.direction === CheckNoteDirection.GIVEN && type === CheckNoteType.CHECK;
      
      // Cari hesap - alınan çek/senet için zorunlu, verilen için opsiyonel
      if (!isGivenCheck && !formData.accountId) {
        setError(t('checksNotes.errors.accountRequired'));
        setLoading(false);
        return;
      }
      
      if (!formData.amount || formData.amount <= 0) {
        setError(t('checksNotes.errors.amountRequired'));
        setLoading(false);
        return;
      }
      
      if (!formData.dueDate) {
        setError(t('checksNotes.errors.dueDateRequired'));
        setLoading(false);
        return;
      }
      
      // Çek numarası defter aralığı kontrolü (kaydetme anında)
      if (selectedCheckBook && formData.checkNumber?.trim()) {
        const num = parseInt(formData.checkNumber.trim(), 10);
        if (!isNaN(num) && (num < selectedCheckBook.startNumber || num > selectedCheckBook.endNumber)) {
          setError(t('checksNotes.errors.checkNumberOutOfRange', { start: selectedCheckBook.startNumber, end: selectedCheckBook.endNumber }));
          setCheckNumberOutOfRange(true);
          setLoading(false);
          return;
        }
      }

      // Çek numarası mükerrer kontrolü (kaydetme anında)
      if (formData.checkNumber?.trim()) {
        try {
          const result = await checkDuplicateCheckNumber(
            formData.checkNumber.trim(),
            type,
            isEdit && editData ? editData.id : undefined
          );
          if (result.isDuplicate) {
            const suggestion = result.firstAvailableNumber
              ? t('checksNotes.form.firstAvailableNumber', { number: result.firstAvailableNumber })
              : '';
            setError(`${t('checksNotes.form.checkNumberAlreadyUsed')}${suggestion}`);
            setCheckNumberDuplicate(true);
            setFirstAvailableCheckNumber(result.firstAvailableNumber);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Mükerrer kontrol hatası:', err);
        }
      }
      
      // Verilen çek için ek validasyonlar
      if (isGivenCheck) {
        if (!formData.checkBookId) {
          setError(t('checksNotes.errors.checkBookRequiredForGiven'));
          setLoading(false);
          return;
        }
      }
      
      if (isEdit && editData) {
        // Güncelleme
        const updateData: UpdateCheckNoteDto = {
          id: editData.id,
          companyId,
          type,
          direction: formData.direction,
          accountId: formData.accountId,
          accountName: selectedAccount?.name || undefined,
          bankName: formData.bankName || undefined,
          bankBranchName: formData.bankBranchName || undefined,
          bankBranchCode: formData.bankBranchCode || undefined,
          checkNumber: formData.checkNumber || undefined,
          checkAccountNumber: formData.checkAccountNumber || undefined,
          drawerName: formData.drawerName || undefined,
          guarantorName: formData.guarantorName || undefined,
          guarantorIdentityNo: formData.guarantorIdentityNo || undefined,
          guarantorAddress: formData.guarantorAddress || undefined,
          debtorIdentityNo: formData.debtorIdentityNo || undefined,
          debtorAddress: formData.debtorAddress || undefined,
          jurisdictionPlace: formData.jurisdictionPlace || undefined,
          issueDate: formData.issueDate,
          issuePlace: formData.issuePlace || undefined,
          dueDate: formData.dueDate,
          amount: formData.amount,
          currency: formData.currency,
          exchangeRate: formData.exchangeRate,
          notes: formData.notes || undefined,
          projectId: formData.projectId
        };
        
        await updateCheckNote(updateData);
        onSave();
      } else {
        // Yeni kayıt
        const createData: CreateCheckNoteDto = {
          companyId,
          type,
          direction: formData.direction,
          accountId: formData.accountId,
          accountName: selectedAccount?.name || undefined,
          bankName: formData.bankName || undefined,
          bankBranchName: formData.bankBranchName || undefined,
          bankBranchCode: formData.bankBranchCode || undefined,
          checkNumber: formData.checkNumber || undefined,
          checkAccountNumber: formData.checkAccountNumber || undefined,
          // Verilen çek için ek alanlar
          bankAccountId: formData.bankAccountId,
          subAccountId: formData.subAccountId,
          checkBookId: formData.checkBookId,
          drawerName: formData.drawerName || undefined,
          guarantorName: formData.guarantorName || undefined,
          guarantorIdentityNo: formData.guarantorIdentityNo || undefined,
          guarantorAddress: formData.guarantorAddress || undefined,
          debtorIdentityNo: formData.debtorIdentityNo || undefined,
          debtorAddress: formData.debtorAddress || undefined,
          jurisdictionPlace: formData.jurisdictionPlace || undefined,
          issueDate: formData.issueDate,
          issuePlace: formData.issuePlace || undefined,
          dueDate: formData.dueDate,
          amount: formData.amount,
          currency: formData.currency,
          exchangeRate: formData.exchangeRate,
          notes: formData.notes || undefined,
          projectId: formData.projectId
        };
        
        await createCheckNote(createData);
        onSave();
      }
    } catch (err: any) {
      console.error('Kaydetme hatası:', err);
      const errorMessage = err.response?.data?.message || err.message || t('checksNotes.errors.saveError');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const isCheck = type === CheckNoteType.CHECK;
  const title = isEdit 
    ? (isCheck ? t('checksNotes.editCheck') : t('checksNotes.editPromissoryNote'))
    : (isCheck ? t('checksNotes.newCheck') : t('checksNotes.newPromissoryNote'));
  
  return (
    <>
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: isCheck ? 'primary.main' : 'secondary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isCheck ? <CheckIcon /> : <DescriptionIcon />}
          <Typography variant="h6">{title}</Typography>
          <GuideButton guideKey="finance.addCheckNote" color="inherit" size="small" />
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ pt: 1 }}>
          {/* Belge Numarası */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('checksNotes.form.documentNumber')}
              value={formData.documentNumber}
              onChange={(e) => handleChange('documentNumber', e.target.value)}
              size="small"
              placeholder={t('checksNotes.form.documentNumberPlaceholder')}
              InputLabelProps={{ 
                sx: { backgroundColor: 'background.paper', px: 0.5 }
              }}
            />
          </Grid>
          
          {/* Yön (Alınan/Verilen) */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small" disabled={isEdit}>
              <InputLabel sx={{ backgroundColor: 'background.paper', px: 0.5 }}>{t('checksNotes.form.direction')}</InputLabel>
              <Select
                value={formData.direction}
                label={t('checksNotes.form.direction')}
                onChange={(e) => handleChange('direction', e.target.value)}
              >
                <MenuItem value={CheckNoteDirection.RECEIVED}>{t('checksNotes.directions.received')}</MenuItem>
                <MenuItem value={CheckNoteDirection.GIVEN}>{t('checksNotes.directions.given')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Para Birimi */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ backgroundColor: 'background.paper', px: 0.5 }}>{t('checksNotes.form.currency')}</InputLabel>
              <Select
                value={formData.currency}
                label={t('checksNotes.form.currency')}
                onChange={(e) => handleChange('currency', e.target.value)}
              >
                <MenuItem value="TRY">{t('checksNotes.form.currencyOptions.try')}</MenuItem>
                <MenuItem value="USD">{t('checksNotes.form.currencyOptions.usd')}</MenuItem>
                <MenuItem value="EUR">EUR - Euro</MenuItem>
                <MenuItem value="GBP">{t('checksNotes.form.currencyOptions.gbp')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Cari Hesap */}
          <Grid item xs={12}>
            <Autocomplete
              options={accounts}
              getOptionLabel={(option) => option ? option.name || '' : ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={selectedAccount}
              onChange={(_, newValue) => handleAccountChange(newValue)}
              loading={loadingAccounts}
              disabled={isEdit}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={option.id} {...otherProps}>
                    <Typography variant="body2">
                      {option.name || ''}
                    </Typography>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('checksNotes.form.account')}
                  size="small"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {isCheck ? t('checksNotes.form.checkInfo') : t('checksNotes.form.promissoryNoteInfo')}
              </Typography>
            </Divider>
          </Grid>
          
          {/* Çek Bilgileri */}
          {isCheck ? (
            <>
              {/* Verilen Çek için Çek Hesabı ve Çek Defteri Seçimi */}
              {formData.direction === CheckNoteDirection.GIVEN && (
                <>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={checkAccounts}
                      getOptionLabel={(option) => option.name}
                      value={selectedCheckAccount}
                      onChange={(_, newValue) => handleCheckAccountSelect(newValue)}
                      loading={loadingCheckAccounts}
                      disabled={isEdit}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <Box component="li" key={option.id} {...otherProps}>
                            <Box>
                              <Typography variant="body2">{option.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.accountNumber} - {option.currency}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('checksNotes.form.checkAccount')}
                          size="small"
                          required
                          placeholder={t('checksNotes.form.checkAccountPlaceholder')}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingCheckAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={checkBooks}
                      getOptionLabel={(option) => `${option.serialNumber} (${option.startNumber}-${option.endNumber})`}
                      value={selectedCheckBook}
                      onChange={(_, newValue) => handleCheckBookSelect(newValue)}
                      loading={loadingCheckBooks}
                      disabled={isEdit || !selectedCheckAccount}
                      noOptionsText={selectedCheckAccount ? t('checksNotes.form.noCheckBookForAccount') : t('checksNotes.form.selectCheckAccountFirst')}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <Box component="li" key={option.id} {...otherProps}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <MenuBookIcon color="primary" fontSize="small" />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2">{option.serialNumber}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t('checksNotes.form.range')}: {option.startNumber}-{option.endNumber} | 
                                  {t('checksNotes.form.remaining')}: {option.totalLeaves - option.usedLeaves} {t('checksNotes.form.leaves')}
                                </Typography>
                              </Box>
                              <Chip 
                                label={t('checksNotes.form.remainingChip', { count: option.totalLeaves - option.usedLeaves })}
                                size="small"
                                color={option.totalLeaves - option.usedLeaves > 5 ? "success" : "warning"}
                              />
                            </Box>
                          </Box>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('checksNotes.form.checkBook')}
                          size="small"
                          required
                          placeholder={selectedCheckAccount ? t('checksNotes.form.selectCheckBook') : t('checksNotes.form.selectCheckAccountFirst')}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <MenuBookIcon fontSize="small" color="action" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                            endAdornment: (
                              <>
                                {loadingCheckBooks ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  {/* Sıradaki çek numarası bilgisi */}
                  {selectedCheckBook && nextCheckNumber && (
                    <Grid item xs={12}>
                      <Alert severity="info" icon={<CheckIcon />}>
                        <Typography variant="body2">
                          <strong>{t('checksNotes.form.nextCheckNumber')}:</strong> {nextCheckNumber} | 
                          <strong> {t('checksNotes.form.checkBookLabel')}:</strong> {selectedCheckBook.serialNumber} | 
                          <strong> {t('checksNotes.form.remainingLeaves')}:</strong> {selectedCheckBook.totalLeaves - selectedCheckBook.usedLeaves}
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                </>
              )}
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('checksNotes.form.bankName')}
                  value={formData.bankName}
                  onChange={(e) => handleChange('bankName', e.target.value)}
                  size="small"
                  disabled={formData.direction === CheckNoteDirection.GIVEN && !!selectedCheckAccount}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('checksNotes.form.bankBranchName')}
                  value={formData.bankBranchName}
                  onChange={(e) => handleChange('bankBranchName', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('checksNotes.form.bankBranchCode')}
                  value={formData.bankBranchCode}
                  onChange={(e) => handleChange('bankBranchCode', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('checksNotes.form.checkNumber')}
                  value={formData.checkNumber}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleChange('checkNumber', val);
                    if (checkNumberDuplicate) {
                      setCheckNumberDuplicate(false);
                      setFirstAvailableCheckNumber(null);
                    }
                    // Defter aralığı kontrolü
                    if (selectedCheckBook && val.trim()) {
                      const num = parseInt(val.trim(), 10);
                      setCheckNumberOutOfRange(!isNaN(num) && (num < selectedCheckBook.startNumber || num > selectedCheckBook.endNumber));
                    } else {
                      setCheckNumberOutOfRange(false);
                    }
                  }}
                  onBlur={handleCheckNumberBlur}
                  size="small"
                  error={checkNumberDuplicate || checkNumberOutOfRange}
                  helperText={
                    checkingDuplicate
                      ? t('checksNotes.form.checking')
                      : checkNumberDuplicate
                        ? `${t('checksNotes.form.checkNumberAlreadyUsed')}${firstAvailableCheckNumber ? t('checksNotes.form.firstAvailableNumber', { number: firstAvailableCheckNumber }) : ''}`
                        : checkNumberOutOfRange && selectedCheckBook
                          ? t('checksNotes.errors.checkNumberOutOfRange', { start: selectedCheckBook.startNumber, end: selectedCheckBook.endNumber })
                          : formData.direction === CheckNoteDirection.GIVEN && selectedCheckBook
                            ? t('checksNotes.form.autoAssignedEditable')
                            : ''
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('checksNotes.form.checkAccountNumber')}
                  value={formData.checkAccountNumber}
                  onChange={(e) => handleChange('checkAccountNumber', e.target.value)}
                  size="small"
                  disabled={formData.direction === CheckNoteDirection.GIVEN && !!selectedCheckAccount}
                />
              </Grid>
            </>
          ) : (
            <>
              {/* Senet Bilgileri */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('checksNotes.form.drawerName')}
                  value={formData.drawerName}
                  onChange={(e) => handleChange('drawerName', e.target.value)}
                  size="small"
                  helperText={t('checksNotes.form.drawerNameHelp')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('checksNotes.form.guarantorName')}
                  value={formData.guarantorName}
                  onChange={(e) => handleChange('guarantorName', e.target.value)}
                  size="small"
                  helperText={t('checksNotes.form.guarantorNameHelp')}
                />
              </Grid>
              
              {/* Borçlu Bilgileri */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('checksNotes.form.debtorIdentityNo')}
                  value={formData.debtorIdentityNo}
                  onChange={(e) => handleChange('debtorIdentityNo', e.target.value)}
                  size="small"
                  inputProps={{ maxLength: 11 }}
                  helperText={t('checksNotes.form.identityNoHelp')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('checksNotes.form.debtorAddress')}
                  value={formData.debtorAddress}
                  onChange={(e) => handleChange('debtorAddress', e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
              
              {/* Kefil Bilgileri */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('checksNotes.form.guarantorIdentityNo')}
                  value={formData.guarantorIdentityNo}
                  onChange={(e) => handleChange('guarantorIdentityNo', e.target.value)}
                  size="small"
                  inputProps={{ maxLength: 11 }}
                  helperText={t('checksNotes.form.guarantorIdentityNoHelp')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('checksNotes.form.guarantorAddress')}
                  value={formData.guarantorAddress}
                  onChange={(e) => handleChange('guarantorAddress', e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
              
              {/* Yetkili Mahkeme */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('checksNotes.form.jurisdictionPlace')}
                  value={formData.jurisdictionPlace}
                  onChange={(e) => handleChange('jurisdictionPlace', e.target.value)}
                  size="small"
                  placeholder={t('checksNotes.form.jurisdictionPlacePlaceholder')}
                  helperText={t('checksNotes.form.jurisdictionPlaceHelp')}
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t('checksNotes.form.dateAndAmount')}
              </Typography>
            </Divider>
          </Grid>
          
          {/* Tarihler ve Tutar */}
          <Grid item xs={12} md={3}>
            <AppDatePicker
              fullWidth
              label={t('checksNotes.form.issueDate')}
              value={formData.issueDate}
              onChange={(value) => handleChange('issueDate', value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t('checksNotes.form.issuePlace')}
              value={formData.issuePlace}
              onChange={(e) => handleChange('issuePlace', e.target.value)}
              size="small"
              placeholder={t('checksNotes.form.issuePlacePlaceholder')}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <AppDatePicker
              fullWidth
              label={t('checksNotes.form.dueDate')}
              value={formData.dueDate}
              onChange={(value) => handleChange('dueDate', value)}
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t('checksNotes.form.amount')}
              type="number"
              value={amountDisplay}
              onChange={(e) => {
                const val = e.target.value;
                setAmountDisplay(val);
                handleChange('amount', val === '' ? 0 : parseFloat(val) || 0);
              }}
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <InputAdornment position="start">{formData.currency}</InputAdornment>,
              }}
            />
          </Grid>
          
          {/* Döviz Kuru (TRY dışındakiler için) */}
          {formData.currency !== 'TRY' && (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t('checksNotes.form.exchangeRate')}
                type="number"
                value={formData.exchangeRate}
                onChange={(e) => handleChange('exchangeRate', parseFloat(e.target.value) || 1)}
                size="small"
                helperText={t('checksNotes.form.exchangeRateAutoHelp')}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      TRY
                      <Tooltip title={t('checksNotes.form.refreshExchangeRateTooltip')}>
                        <IconButton
                          size="small"
                          onClick={handleRefreshExchangeRate}
                          disabled={loadingExchangeRate}
                          sx={{ ml: 0.5 }}
                        >
                          {loadingExchangeRate ? (
                            <CircularProgress size={16} />
                          ) : (
                            <RefreshIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}
          
          {/* Notlar */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('checksNotes.form.notes')}
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              size="small"
              multiline
              rows={2}
            />
          </Grid>

          {/* Proje bağı (PYS) */}
          <Grid item xs={12} sm={6}>
            <ProjectSelector
              value={formData.projectId}
              onChange={(value) => handleChange('projectId', value)}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('checksNotes.form.cancel')}
        </Button>
        <Button 
          variant="contained" 
          color={isCheck ? 'primary' : 'secondary'}
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {isEdit ? t('checksNotes.form.update') : t('checksNotes.form.save')}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Kapatma Onay Dialogı */}
    <Dialog
      open={confirmCloseOpen}
      onClose={() => setConfirmCloseOpen(false)}
      maxWidth="xs"
    >
      <DialogTitle>{t('checksNotes.form.confirmCloseTitle')}</DialogTitle>
      <DialogContent>
        <Typography>
          {t('checksNotes.form.confirmCloseMessage')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmCloseOpen(false)}>
          {t('checksNotes.form.confirmCloseStay')}
        </Button>
        <Button onClick={handleConfirmClose} color="error" variant="contained">
          {t('checksNotes.form.confirmCloseLeave')}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default AddCheckNoteModal;
